import {
  ActionIcon,
  Box,
  Button,
  CloseIcon,
  Flex,
  Group,
  Paper,
  Popover,
  rem,
  Space,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import {
  ScatterChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Scatter,
  ResponsiveContainer,
} from "recharts";
import { LikeResponse } from "../../../entities/components/LikeResponse";
import { useMediaQuery } from "@mantine/hooks";
import useReportStore from "../../../store/useReportStore";
import { useTranslation } from "react-i18next";
import { getCurrencySymbol } from "../../../utils/helpers";
import { getJobDetailsByTitle } from "../../../api/reportApi";
import { useEffect, useRef, useState } from "react";
import { JobPositionData } from "../../../types/api";
import { JobDetailsModal } from "../../../utils/components/JobDetailsModal";
import { InfoTooltipIcon } from "../../../shared/svg/InfoTooltopIcon";
import useProfileStore from "../../../store/useProfileStore";
import { OnboardingTour } from "@gfazioli/mantine-onboarding-tour";
import { ReportCreationModal } from "../utils/ReportCreationModal";
import {
  IconDotsVertical,
  IconDotsVerticalSmall,
} from "../../../shared/svg/IconDots";

const COLORS = [
  "rgba(5, 216, 210, 0.6)",
  "rgba(166, 77, 215, 0.6)",
  "rgba(111, 64, 226, 0.6)",
  "rgba(58, 139, 217, 0.6)",
];

// Функція для масштабування значення
const scaleValue = (
  value: number,
  min: number,
  max: number,
  newMin: number,
  newMax: number
) => {
  return ((value - min) / (max - min)) * (newMax - newMin) + newMin;
};

export const OptimalPositions = () => {
  const { report, reportId, updatePreferences } = useReportStore();
  const matches = useMediaQuery("(max-width: 1100px)");
  const mobile = useMediaQuery("(max-width: 650px)");
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [currentJob, setCurrentJob] = useState<JobPositionData | null>(null);
  const theme = useMantineTheme();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const ref = useRef(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPopoverOpen(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  const { profileLanguage } = useProfileStore();

  const handleOpen = async (title: string) => {
    try {
      const res = await getJobDetailsByTitle(
        title,
        Object.keys(profileLanguage!)[0]
      );
      if (res) {
        setCurrentJob(res.data);
        setOpened(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const commonStyles: React.CSSProperties = {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.dark[7],
    fontSize: rem(14),
    padding: `${rem(4)} ${rem(8)}`,
    borderRadius: rem(4),
    boxShadow: theme.shadows.sm,
    maxWidth: rem(500),
    wordWrap: "break-word" as "break-word",
    whiteSpace: "normal",
    border: "1px solid gray",
  };

  const tooltipStyles = {
    borderRadius: rem(10),
    backgroundColor: theme.colors.violet[7],
    color: theme.white,
    padding: rem(12),
    boxShadow: theme.shadows.lg,
    border: `1px solid ${theme.colors.gray[5]}`,
    maxWidth: mobile ? rem(300) : rem(400),
    zIndex: 1,
  };

  const positions = report?.optimal_positions_switch?.mark_down
    ? report.optimal_positions_switch
    : report?.optimal_positions?.mark_down
    ? report.optimal_positions
    : null;

  // Мінімальні та максимальні значення для вашого графіка
  const min = -10;
  const max = 115;
  const newMin = 0;
  const newMax = 100;

  const data =
    positions?.data.map((role, index) => ({
      // Масштабуємо значення
      x: scaleValue(role.fit_percentage, min, max, newMin, newMax),
      y: scaleValue(role.job_stability, min, max, newMin, newMax),
      label: role.title,
      value: role.average_salary,
      color: COLORS[index % COLORS.length],
    })) || [];

  const CustomBubble = (props: any) => {
    const { cx, cy, payload } = props;
    const radius = payload.x;
    const calculatedSize = radius * (matches ? 1.5 : 2);
    const realSize = calculatedSize > 80 ? calculatedSize : 80;

    return (
      <foreignObject
        x={cx - radius}
        y={cy - radius}
        width={realSize}
        height={realSize}
        color="#fff"
        style={{
          borderRadius: "50%",
          fontSize: "10px",
        }}
      >
        <Paper radius="50%" bg={payload.color}>
          <Flex
            w={realSize}
            h={realSize}
            align="center"
            justify="center"
            direction="column"
            gap={4}
          >
            <Text size="10px" c="#000" ta="center" w={realSize * 0.9}>
              {payload.label}
            </Text>
            <Text c="#000" ta="center" fw="bold" size="14px">
              {payload.value / 1000}K
            </Text>
          </Flex>
        </Paper>
      </foreignObject>
    );
  };

  return (
    <Paper radius="24px" p="32px" mt={32} withBorder>
      <Flex w="100%" justify="space-between" align="center">
        <Title size={matches ? 24 : 36} mb={8}>
          {positions?.mark_down
            ? positions?.mark_down?.replace(/^## /, "")
            : ""}
        </Title>
        <OnboardingTour.Target id="button-edit-section">
          <div ref={ref}>
            <Popover
              opened={
                popoverOpen && report.ui_preferences.show_context_override_hint
              }
              onChange={setPopoverOpen}
              position="top"
              withArrow
              offset={6}
              arrowSize={8}
              transitionProps={{ duration: 100, transition: "fade" }}
              zIndex={1}
              styles={{
                dropdown: tooltipStyles,
                arrow: {
                  backgroundColor: theme.colors.violet[7],
                  border: "1px solid gray",
                },
              }}
            >
              <Popover.Target>
                <ActionIcon
                  variant="light"
                  color="gray"
                  onClick={() => setIsCreateOpen(true)}
                  w={36}
                  h={36}
                >
                  <IconDotsVertical />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <Flex
                  justify="space-between"
                  gap="xs"
                  mb="xs"
                  align="flex-start"
                >
                  <Text
                    style={{
                      lineHeight: 1.5,
                      wordBreak: "break-word",
                    }}
                  >
                    {t("generate_tooltip") + " "}
                    <Box
                      component="span"
                      w={22}
                      h={22}
                      ml={4}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: theme.radius.sm,
                        backgroundColor: theme.colors.gray[2],
                        color: theme.colors.gray[7],
                        verticalAlign: "middle",
                      }}
                    >
                      <IconDotsVerticalSmall />
                    </Box>
                  </Text>

                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="white"
                    onClick={() => setPopoverOpen(false)}
                  >
                    <CloseIcon />
                  </ActionIcon>
                </Flex>

                <Flex
                  align="flex-start"
                  mt="22px"
                  justify="space-between"
                  gap={20}
                >
                  <Text
                    onClick={() => {
                      setPopoverOpen(false);
                      updatePreferences(reportId, false);
                    }}
                    style={{
                      fontSize: matches ? rem(14) : rem(16),
                      pointerEvents: "all",
                      cursor: "pointer",
                      textDecoration: "underline",
                      color: theme.colors.violet[1],
                      lineHeight: 1.4,
                    }}
                  >
                    {t("not_show_tooltip")}
                  </Text>

                  <Button
                    size="xs"
                    style={{
                      flexShrink: 0,
                    }}
                    onClick={() => {
                      setPopoverOpen(false);
                      setIsCreateOpen(true);
                    }}
                  >
                    {t("do_it_now")}
                  </Button>
                </Flex>
              </Popover.Dropdown>
            </Popover>
          </div>
        </OnboardingTour.Target>
      </Flex>

      <Text style={{ fontSize: matches ? 16 : 18 }} mb={24} c="dimmed">
        {positions ? positions?.explanation : ""}
      </Text>
      <Text fw="bold" mb={10}>
        {t("job_stability")} %
      </Text>
      <ResponsiveContainer height={matches ? 538 : 680} width="100%">
        <ScatterChart data={data} margin={{ left: -25 }}>
          <CartesianGrid />
          <XAxis
            type="number"
            dataKey="x"
            name="X-axis"
            domain={[newMin, newMax]}
          />
          <YAxis
            type="number"
            dataKey="y"
            name="Y-axis"
            domain={[newMin, newMax]}
          />
          {/* <Tooltip cursor={{ strokeDasharray: "3 3" }} /> */}
          <Scatter name="Positions" data={data} shape={CustomBubble} />
        </ScatterChart>
      </ResponsiveContainer>
      <Text fw="bold" ta="right">
        {t("match")} %
      </Text>
      {positions?.data.map((position, index) => (
        <div key={index}>
          <Flex align="center" mt={24} gap={5}>
            <Text fw="bold" size="20px">
              {position.title}
            </Text>
            <Tooltip
              label={t("learn_more")}
              withArrow
              transitionProps={{ duration: 100, transition: "fade" }}
              arrowSize={8}
              arrowOffset={8}
              position="top"
              offset={6}
              styles={{
                tooltip: commonStyles,
                arrow: {
                  backgroundColor: theme.colors.gray[1],
                  border: "1px solid gray",
                },
              }}
            >
              <ActionIcon
                variant="subtle"
                size="sm"
                c="gray"
                style={{ verticalAlign: "middle" }}
                aria-label="More actions"
                onClick={() => handleOpen(position.title)}
              >
                <InfoTooltipIcon size={20} />
              </ActionIcon>
            </Tooltip>
          </Flex>

          <Space h={5} />
          <Text size={matches ? "m" : "lg"}>
            <b>{t("average_salary")}</b>:{" "}
            {position?.average_salary?.toLocaleString("en-US")}
            {getCurrencySymbol(position.salary_currency) || "$"}
          </Text>
          <Space h={5} />
          <Text size={matches ? "m" : "lg"}>{position.description}</Text>
        </div>
      ))}
      <ReportCreationModal
        opened={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />
      <JobDetailsModal
        opened={opened}
        job={currentJob!}
        onClose={() => {
          setOpened(false);
          setCurrentJob(null);
        }}
        setCurrentJob={setCurrentJob}
      />
      <LikeResponse report_id={reportId} section="optimal_positions_switch" />
    </Paper>
  );
};
